/**
 * Keyword clusters — AdSense-safe (gambling, tobacco, piracy, off-topic
 * commercial noise filtered out per plan). Consumed by GoldCityAliasPage,
 * PakistanMatrixPage, CountryPage, SilverPage, UAE/Saudi routes to render
 * a "Related searches on DrHint" chip grid and cluster-derived FAQs.
 *
 * Every chip must map to an EXISTING route — no dead links.
 */

import type { Country } from "@/lib/gold-data";

export type KwLink = { label: string; href: string };

/** Country-level clusters ------------------------------------------------ */

export const COUNTRY_KW: Record<"pakistan" | "india" | "uae" | "saudi", KwLink[]> = {
  pakistan: [
    { label: "gold price in pakistan today", href: "/pakistan" },
    { label: "gold rate in pakistan today per tola", href: "/pakistan/per-tola" },
    { label: "gold current rate in pakistan", href: "/pakistan" },
    { label: "gold 1 gram price in pakistan today", href: "/pakistan/per-gram" },
    { label: "gold rate 22 carat in pakistan today", href: "/pakistan/22k" },
    { label: "gold 24k rate in pakistan today", href: "/pakistan/24k" },
    { label: "gold 21 carat price in pakistan", href: "/pakistan/21k" },
    { label: "gold 18 carat price in pakistan today", href: "/pakistan/18k" },
    { label: "gold rate 10 gram pakistan", href: "/pakistan/per-10-gram" },
    { label: "gold to pkr", href: "/pakistan" },
    { label: "gold market open time pakistan", href: "/pakistan" },
    { label: "yesterday gold rate pakistan", href: "/pakistan/yesterday" },
    { label: "gold vs silver pakistan", href: "/silver-rate-pakistan" },
    { label: "gold zakat calculator pakistan", href: "/pakistan/zakat-nisab" },
    { label: "pakistan vs india gold rate", href: "/pakistan/vs/india" },
  ],
  india: [
    { label: "gold price today in india", href: "/india" },
    { label: "gold rate today in india", href: "/india" },
    { label: "gold and silver price today in india", href: "/silver-rate-india" },
    { label: "22 carat gold rate today india", href: "/india" },
    { label: "24k gold price today india", href: "/india" },
    { label: "gold rate today per 10 gram india", href: "/india" },
    { label: "1 gram gold rate today india", href: "/india" },
    { label: "gold import duty india", href: "/india" },
    { label: "where gold is cheapest in india", href: "/india" },
    { label: "how gold price is decided in india", href: "/india" },
    { label: "gold monetization scheme india", href: "/india" },
    { label: "sovereign gold bond india", href: "/india" },
    { label: "silver rate today india", href: "/silver-rate-india" },
    { label: "gold loan calculator india", href: "/calculators" },
    { label: "aaj ka sone ka bhav", href: "/aaj-ka-sone-ka-bhav" },
    { label: "sone ka bhav aaj ka", href: "/aaj-ka-sone-ka-bhav" },
    { label: "sone ka bhav kya hai aaj ka", href: "/aaj-ka-sone-ka-bhav" },
    { label: "22 carat sone ka bhav aaj ka", href: "/sone-ka-bhav-22-carat" },
    { label: "24 carat sone ka bhav aaj ka", href: "/india" },
    { label: "18 carat sone ka bhav aaj ka", href: "/india" },
    { label: "1 tola sone ka bhav", href: "/india" },
    { label: "10 gram sone ka bhav", href: "/india" },
    { label: "1 gram sone ka bhav", href: "/india" },
    { label: "1 kilo sone ka bhav", href: "/india" },
    { label: "live sone ka bhav", href: "/aaj-ka-sone-ka-bhav" },
    { label: "today sone ka bhav", href: "/aaj-ka-sone-ka-bhav" },
    { label: "sone ka bhav kab kam hoga", href: "/india/gold-forecast" },
    { label: "chandi aur sone ka bhav aaj ka", href: "/silver-rate-india" },
    { label: "sone ka bhav delhi", href: "/gold-rate-delhi" },
    { label: "sone ka bhav mumbai", href: "/gold-rate-mumbai" },
    { label: "sone ka bhav jaipur", href: "/city/india/jaipur" },
    { label: "sone ka bhav lucknow", href: "/city/india/lucknow" },
    { label: "sone ka bhav pune", href: "/city/india/pune" },
    { label: "sone ka bhav surat", href: "/city/india/surat" },
    { label: "sone ka bhav indore", href: "/city/india/indore" },
    { label: "sone ka bhav bhopal", href: "/city/india/bhopal" },
    { label: "sone ka bhav nagpur", href: "/city/india/nagpur" },
    { label: "sone ka bhav kanpur", href: "/city/india/kanpur" },
    { label: "up mein sone ka bhav", href: "/india" },
    { label: "rajasthan mein sone ka bhav", href: "/india" },
  ],
  uae: [
    { label: "dubai gold rate today", href: "/uae" },
    { label: "uae gold rate today 22k", href: "/uae" },
    { label: "uae gold rate today 24k", href: "/uae" },
    { label: "uae gold rate today 21k", href: "/uae" },
    { label: "uae gold rate today 18k", href: "/uae" },
    { label: "dubai gold souk price today", href: "/uae" },
    { label: "uae gold rate live per gram", href: "/uae" },
    { label: "rate of gold in dubai", href: "/uae" },
  ],
  saudi: [
    { label: "saudi gold price today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "saudi gold rate 24k today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "saudi gold rate 22k today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "saudi gold rate 21k today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "saudi gold rate 18k today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "gold price in riyadh today", href: "/ar/gold-rate-saudi-arabia" },
    { label: "gold price in jeddah today", href: "/ar/gold-rate-saudi-arabia" },
  ],
};

/** Karat-level clusters -------------------------------------------------- */

export const KARAT_KW: Record<"24k" | "22k" | "21k" | "18k", string[]> = {
  "24k": [
    "24 carat gold rate today",
    "24k gold price today",
    "gold 999 price today",
    "gold 999.9 price today",
    "gold 995 means",
    "todays 24 carat gold rate",
    "24k gold rate per gram today",
    "24k gold rate per tola today",
    "24 carat gold rate per 10 gram today",
  ],
  "22k": [
    "22 carat gold rate today",
    "22k gold price today",
    "916 gold rate today",
    "916 gold means",
    "22kt gold rate today",
    "22 carat gold rate per gram today",
    "22 carat gold rate per tola today",
    "22 carat gold rate per 10 gram today",
  ],
  "21k": [
    "21 carat gold rate today",
    "21k gold price today",
    "gold 21 carat price today",
    "875 gold means",
    "21k gold rate per gram today",
    "21k gold rate per tola today",
  ],
  "18k": [
    "18 carat gold rate today",
    "18k gold price today",
    "750 gold rate today",
    "750 hallmark gold price today",
    "gold 750 means",
    "18k gold rate per gram today",
    "18k gold rate per tola today",
  ],
};

/** Weight-level clusters ------------------------------------------------- */

export const WEIGHT_KW: Record<"per-tola" | "per-gram" | "per-10-gram", string[]> = {
  "per-tola": [
    "1 tola gold price today",
    "3 tola gold price today",
    "5 tola gold price today",
    "10 tola gold price today",
    "1 tola gold price in pakistan today",
    "gold rate per tola today",
  ],
  "per-gram": [
    "1 gram gold rate today",
    "gold 1 gram price today",
    "4 gram gold rate today",
    "5 gram gold price today",
    "6 gram gold rate today",
    "8 gram gold rate today",
    "gold rate per gram today",
  ],
  "per-10-gram": [
    "10 gram gold price today",
    "gold rate 10 gram today",
    "30 gram gold price today",
    "50 gram gold price today",
    "100 gram gold price today",
    "1 kg gold price today",
  ],
};

/** City-specific clusters — override generator for high-search cities.
 *  Any city not in this map uses buildCityKw() fallback. */

const cityFallbackKw = (country: Country, citySlug: string, cityName: string): string[] => {
  const isPk = country === "pakistan";
  const unit = isPk ? "per tola" : "per 10 gram";
  return [
    `gold rate today in ${cityName.toLowerCase()}`,
    `${cityName.toLowerCase()} today gold rate`,
    `${cityName.toLowerCase()} gold price today`,
    `22 carat gold rate today in ${cityName.toLowerCase()}`,
    `24 carat gold rate today in ${cityName.toLowerCase()}`,
    `21 carat gold rate today in ${cityName.toLowerCase()}`,
    `18 carat gold rate today in ${cityName.toLowerCase()}`,
    `${cityName.toLowerCase()} gold rate ${unit}`,
    `1 gram gold rate ${cityName.toLowerCase()}`,
    isPk ? `sona ka bhao ${cityName.toLowerCase()}` : `sone ka bhav ${cityName.toLowerCase()}`,
  ];
};

// High-value city overrides (from user's cluster) — merged with fallback.
const CITY_OVERRIDES: Record<string, string[]> = {
  delhi: ["delhi today gold rate", "24 carat gold rate in delhi today", "gold price today delhi", "916 gold rate delhi", "1 tola gold price delhi", "delhi mein sone ka bhav", "delhi mein aaj ka sone ka bhav", "sone ka bhav delhi"],
  mumbai: ["mumbai gold price today", "today gold rate in mumbai", "rate of gold in mumbai today", "zaveri bazaar gold rate today", "sone ka bhav mumbai", "mumbai mein sone ka bhav"],
  chennai: ["gold rate today chennai", "gold and silver rate today chennai", "22 carat gold rate today chennai", "1 sovereign gold price chennai", "sone ka bhav chennai"],
  kolkata: ["kolkata gold rate today", "24 carat gold price in kolkata today", "916 gold rate kolkata", "sone ka bhav kolkata"],
  bangalore: ["today gold rate bangalore", "916 gold rate today bangalore", "24k gold rate today bangalore", "bhima jewellers gold rate today", "sone ka bhav bangalore"],
  "hyderabad-in": ["gold and silver rate today hyderabad", "today gold rate in hyderabad", "22 carat gold rate hyderabad", "sone ka bhav hyderabad"],
  jaipur: ["jaipur gold price today", "jaipur gold rate today", "gold rate jaipur 22 carat", "sone ka bhav jaipur", "rajasthan mein sone ka bhav"],
  pune: ["gold rate today pune", "pune gold rate today", "22 carat gold rate pune", "sone ka bhav pune"],
  coimbatore: ["8 gram gold rate today in coimbatore", "gold rate today coimbatore", "22 carat gold rate coimbatore"],
  patna: ["750 gold rate in patna today", "gold rate today patna", "24 carat gold rate patna"],
  kanpur: ["kanpur gold rate today", "22 carat gold rate kanpur", "silver rate today kanpur", "sone ka bhav kanpur"],
  lucknow: ["lucknow gold price today", "gold rate lucknow today", "24 carat gold rate lucknow", "sone ka bhav lucknow", "lucknow mein sone ka bhav", "up mein sone ka bhav"],
  bhopal: ["bhopal gold rate today", "22 carat gold rate bhopal", "sone ka bhav bhopal"],
  ludhiana: ["ludhiana gold price today", "gold rate ludhiana today"],
  ahmedabad: ["ahmedabad gold rate today", "22 carat gold rate ahmedabad", "sone ka bhav ahmedabad"],
  surat: ["surat gold rate today", "22 carat gold rate surat", "sone ka bhav surat"],
  nagpur: ["nagpur gold rate today", "22 carat gold rate nagpur", "sone ka bhav nagpur"],
  indore: ["indore gold rate today", "22 carat gold rate indore", "sone ka bhav indore"],
  vadodara: ["vadodara gold rate today", "22 carat gold rate vadodara"],
  visakhapatnam: ["visakhapatnam gold rate today", "22 carat gold rate visakhapatnam"],
  karachi: ["gold rate today karachi", "karachi sarafa bazaar rate", "1 tola gold price karachi today", "22 carat gold rate karachi"],
  lahore: ["gold rate today lahore", "lahore sarafa bazaar rate", "22 carat gold rate lahore today", "1 tola gold price lahore"],
  islamabad: ["gold rate today islamabad", "islamabad gold rate 22 carat today", "24k gold rate islamabad"],
  rawalpindi: ["gold rate today rawalpindi", "rawalpindi sarafa bazaar rate", "22 carat gold rate rawalpindi"],
  peshawar: ["gold rate today peshawar", "peshawar sarafa bazaar rate", "22 carat gold rate peshawar"],
  quetta: ["gold rate today quetta", "22 carat gold rate quetta today"],
  multan: ["gold rate today multan", "multan sarafa bazaar rate"],
  faisalabad: ["gold rate today faisalabad", "faisalabad sarafa bazaar rate", "22 carat gold rate faisalabad"],
  "hyderabad-pk": ["gold rate today hyderabad sindh", "hyderabad pakistan gold rate"],
  sialkot: ["gold rate today sialkot", "22 carat gold rate sialkot"],
  gujranwala: ["gold rate today gujranwala", "gujranwala sarafa bazaar rate", "22 carat gold rate gujranwala today", "1 tola gold price gujranwala"],
};

/** Hindi tail phrase for an Indian city — used in H2 subhead + meta of
 *  India city alias pages. Returns empty string for non-India cities. */
const INDIA_CITY_HINDI_TAIL: Record<string, string> = {
  delhi: "Delhi Mein Sone Ka Bhav",
  mumbai: "Mumbai Mein Sone Ka Bhav",
  chennai: "Chennai Mein Sone Ka Bhav",
  kolkata: "Kolkata Mein Sone Ka Bhav",
  bangalore: "Bangalore Mein Sone Ka Bhav",
  "hyderabad-in": "Hyderabad Mein Sone Ka Bhav",
  ahmedabad: "Ahmedabad Mein Sone Ka Bhav",
  pune: "Pune Mein Sone Ka Bhav",
  jaipur: "Jaipur Mein Sone Ka Bhav — Rajasthan Rate",
  lucknow: "Lucknow Mein Sone Ka Bhav — UP Rate",
  surat: "Surat Mein Sone Ka Bhav",
  kanpur: "Kanpur Mein Sone Ka Bhav",
  nagpur: "Nagpur Mein Sone Ka Bhav",
  indore: "Indore Mein Sone Ka Bhav",
  bhopal: "Bhopal Mein Sone Ka Bhav",
  patna: "Patna Mein Sone Ka Bhav",
  vadodara: "Vadodara Mein Sone Ka Bhav",
  ludhiana: "Ludhiana Mein Sone Ka Bhav",
  coimbatore: "Coimbatore Mein Sone Ka Bhav",
  visakhapatnam: "Visakhapatnam Mein Sone Ka Bhav",
};

export function indiaCityHindiTail(citySlug: string): string {
  return INDIA_CITY_HINDI_TAIL[citySlug] ?? "";
}

export function cityKeywords(country: Country, citySlug: string, cityName: string): string[] {
  const bespoke = CITY_OVERRIDES[citySlug] ?? [];
  const fallback = cityFallbackKw(country, citySlug, cityName);
  return Array.from(new Set([...bespoke, ...fallback])).slice(0, 14);
}

/** Build per-city related-links (chip grid) — combines city + country
 *  + karat + weight anchors, each mapped to an existing internal route. */
export function cityRelatedLinks(country: Country, citySlug: string, cityName: string): KwLink[] {
  const isPk = country === "pakistan";
  const countryHref = isPk ? "/pakistan" : "/india";
  const otherCityRoute = "/city/$country/$city";
  const chips: KwLink[] = [];

  const cityKw = cityKeywords(country, citySlug, cityName);
  // First 6 city-scoped keywords → own city page
  cityKw.slice(0, 6).forEach((label) => chips.push({ label, href: `/${country}/${citySlug}` }));

  // Karat sub-pages (PK only — India karat routing lives on hub cards)
  if (isPk) {
    chips.push({ label: `24k gold rate ${cityName.toLowerCase()}`, href: "/pakistan/24k" });
    chips.push({ label: `22k gold rate ${cityName.toLowerCase()}`, href: "/pakistan/22k" });
    chips.push({ label: `21k gold rate ${cityName.toLowerCase()}`, href: "/pakistan/21k" });
    chips.push({ label: `18k gold rate ${cityName.toLowerCase()}`, href: "/pakistan/18k" });
    chips.push({ label: `1 tola gold price ${cityName.toLowerCase()}`, href: "/pakistan/per-tola" });
    chips.push({ label: `10 gram gold rate ${cityName.toLowerCase()}`, href: "/pakistan/per-10-gram" });
  }
  // Country-level anchors
  chips.push({ label: isPk ? "gold price in pakistan today" : "gold price today in india", href: countryHref });
  chips.push({ label: isPk ? "silver rate pakistan today" : "silver rate today india", href: isPk ? "/silver-rate-pakistan" : "/silver-rate-india" });
  chips.push({ label: isPk ? "gold zakat calculator" : "zakat calculator", href: isPk ? "/pakistan/zakat-nisab" : "/zakat-calculator" });
  chips.push({ label: `city-wise gold rate ${isPk ? "pakistan" : "india"}`, href: isPk ? "/pakistan/cities" : "/india/cities" });
  // (keeping otherCityRoute variable used to silence lint)
  void otherCityRoute;
  return chips.slice(0, 18);
}

/** FAQ pairs derived from a city's cluster — merged into GoldCityAliasPage. */
export function cityClusterFaqs(country: Country, citySlug: string, cityName: string): { q: string; a: string }[] {
  const isPk = country === "pakistan";
  const unit = isPk ? "per tola" : "per 10 gram";
  const currency = isPk ? "PKR" : "₹";
  const lower = cityName.toLowerCase();
  return [
    {
      q: `${cityName} mein 22 carat gold ka aaj rate kya hai?`,
      a: `${cityName} ka live 22K (916 hallmark) rate 24K base ka 91.67% hota hai — is page ke "Full breakdown" cards mein 22K ${unit} figure live update ho raha hai. 22K jewellery ke liye standard purity hai; making charge (3–15%) alag se jeweller invoice par add hoti hai.`,
    },
    {
      q: `${cityName} mein 24k gold rate per gram kya hai aaj?`,
      a: `24K per-gram rate = per tola ÷ 11.6638 (${isPk ? "Pakistan" : "India"}). ${cityName} ke liye ye live figure page ke headline card mein "Per Gram" ke sath show ho raha hai — international spot ${isPk ? "+ USD/PKR" : "+ USD/INR"} se derive hota hai har 10 minute mein.`,
    },
    {
      q: `Kya ${cityName} mein 1 tola gold price aaj barh gaya ya kam hua?`,
      a: `Aaj ka intraday change is page ke chart section mein visible hai — last-6-months line chart aaj ke price ko right edge par anchor karta hai, jisse aap short-term direction judge kar sakte hain. Yesterday closing se change ka absolute ${currency} value bhi live displayed hai.`,
    },
    {
      q: `${cityName} sarafa bazaar aur is site ka rate same kyun nahi hota?`,
      a: `${cityName} sarafa bazaar ka daily announcement per-day fixed hota hai (usually morning), jabke ye page international spot + FX rate ke live basis par har 10 minute par recalculate karta hai. Difference typically 0.2–0.8% intraday hota hai — long-term trend same hi rehti hai.`,
    },
    {
      q: `${cityName} mein gold buy karne se pehle kya check karna chahiye?`,
      a: `Teen cheezein: (1) BIS/PSQCA hallmark stamp on the piece (999 for 24K, 916 for 22K, 875 for 21K, 750 for 18K), (2) making charge — % ya per-gram flat, (3) buy-back policy of the jeweller. ${cityName} mein reputable jewellers written invoice + hallmark certificate ke sath dete hain — receipt zaroor keep karo zakat aur resale ke liye.`,
    },
    {
      q: `${cityName} mein gold ki price kyun different hoti hai baaki cities se?`,
      a: `Base national rate same hai; ${cityName} ka rate local premium (transport, insurance, jeweller margin) ke wajah se 0.05–0.4% up/down ho sakta hai. Wedding season (${isPk ? "Oct–Feb" : "Nov–Feb + Apr–May"}) mein demand-driven premium 0.5–1.2% zyada hoti hai. Live premium is page ke "City Facts" strip mein show ho raha hai.`,
    },
  ];
}

/** Karat-page cluster FAQs (used by /pakistan/{karat} routes) */
export function karatClusterFaqs(karat: "24k" | "22k" | "21k" | "18k", country: Country = "pakistan"): { q: string; a: string }[] {
  const isPk = country === "pakistan";
  const purityText: Record<typeof karat, string> = {
    "24k": "99.9% pure (999 hallmark)",
    "22k": "91.67% pure (916 hallmark)",
    "21k": "87.5% pure (875 hallmark)",
    "18k": "75% pure (750 hallmark)",
  };
  const meansMap: Record<typeof karat, string> = {
    "24k": "999 / 999.9",
    "22k": "916",
    "21k": "875",
    "18k": "750",
  };
  return [
    {
      q: `What does gold ${meansMap[karat]} mean?`,
      a: `${meansMap[karat]} is the hallmark stamp indicating ${purityText[karat]}. Every ${karat.toUpperCase()} jewellery / bar sold by a registered jeweller in ${isPk ? "Pakistan (PSQCA)" : "India (BIS)"} must carry this mark. Ye stamp piece ke inside face par laser-etched hoti hai — buy karte waqt magnifier se check kar sakte hain.`,
    },
    {
      q: `${karat.toUpperCase()} gold rate today per tola kya hai?`,
      a: `${karat.toUpperCase()} per-tola rate = 24K base rate × ${karat === "24k" ? "1.0" : karat === "22k" ? "0.9167" : karat === "21k" ? "0.875" : "0.75"}. Is page ke city-wise breakdown table mein live figure har city ke liye alag show ho raha hai — Karachi base se lekar 20+ sarafa bazaars tak.`,
    },
    {
      q: `${karat.toUpperCase()} vs other purities — kaunsa buy karna behtar hai?`,
      a: karat === "24k"
        ? "24K sirf investment (coins, bars, ETF-backing) ke liye best hai — jewellery ke liye bahut soft hota hai, easily bend hota hai. Regular wear ke liye 22K ya 21K choose karo."
        : karat === "22k"
          ? "22K jewellery ka global standard hai — Pakistan/India wedding sets, chains, bangles sab 22K (916) mein bante hain. Investment ke liye 24K coins, daily wear ke liye 22K jewellery — perfect combo."
          : karat === "21k"
            ? "21K UAE / Gulf-style daily-wear jewellery ka standard hai — 22K se thoda harder, more affordable, aur design flexibility better hai. Pakistan mein bhi 21K light-weight sets ke liye popular hai."
            : "18K European / western-style jewellery aur high-end fashion pieces ke liye common hai — 25% alloy content isse harder aur cheaper banata hai. Diamond-set jewellery mostly 18K mein banti hai kyunki setting strong hoti hai.",
    },
  ];
}

/** Weight-page cluster FAQs (used by /pakistan/{per-tola|per-gram|per-10-gram}) */
export function weightClusterFaqs(unit: "per-tola" | "per-gram" | "per-10-gram"): { q: string; a: string }[] {
  const unitLabel = unit === "per-tola" ? "per tola" : unit === "per-gram" ? "per gram" : "per 10 gram";
  const gramsPer = unit === "per-tola" ? "11.6638 gram" : unit === "per-gram" ? "1 gram" : "10 gram";
  return [
    {
      q: `1 ${unit === "per-tola" ? "tola" : unit === "per-gram" ? "gram" : "10 gram"} gold price today kya hai?`,
      a: `Aaj Pakistan mein 1 ${gramsPer} sone ka rate live is page ke top card mein show ho raha hai — 24K, 22K, 21K aur 18K teenon ke liye alag alag. Ye rate international spot XAU/USD ÷ 31.1035 × USD/PKR × ${unit === "per-tola" ? "11.6638" : unit === "per-gram" ? "1" : "10"} formula par based hai.`,
    },
    {
      q: `3 tola / 5 tola / 10 tola gold ka rate kaise calculate karein?`,
      a: `Simple multiplication: live 1-tola rate × N (jitna tola chahiye). Bulk buy par jeweller aksar 0.3–0.8% concession deta hai (labour + margin adjustment). Is page ke bulk table mein 3, 5, 10 tola figures already calculated hain live rate se.`,
    },
    {
      q: `Per gram vs per tola vs per 10 gram — kaunsi unit standard hai?`,
      a: `Pakistan mein sarafa bazaar per-tola quote karta hai (default). India mein per-10-gram (IBJA/MCX standard) use hoti hai. Per-gram international / Gulf / western markets ka default hai. Conversion: 1 tola = 11.6638 gram; 10 gram = 0.8573 tola. Is page ka rate ${unitLabel} default hai, baaki units cards mein visible hain.`,
    },
  ];
}

/** Country-hub cluster FAQs (extra, complements existing hubFaqs) */
export const COUNTRY_CLUSTER_FAQS: Record<"pakistan" | "india" | "uae" | "saudi", { q: string; a: string }[]> = {
  pakistan: [
    { q: "Gold market open time in Pakistan kya hai?", a: "Sarafa bazaar Pakistan mein Monday–Saturday, 10:00 AM se 8:00 PM PKT tak open rehta hai (Karachi, Lahore, Islamabad, Rawalpindi). Sunday most bazaars closed hote hain except select shops. Live international spot 24×7 chalta hai — is site ka rate spot + PKR se derive hoke Sunday bhi update hota rehta hai." },
    { q: "Pakistan mein gold highest rate 2026 mein kitna gaya?", a: "2026 mein Pakistan ka 24K per-tola rate multiple all-time highs cross kar chuka hai — driven by international spot record + PKR pressure. Exact peak figure is site ke /pakistan/history page par annual chart ke sath visible hai." },
  ],
  india: [
    { q: "How gold price is decided in India — IBJA formula?", a: "IBJA (India Bullion & Jewellers Association) daily AM/PM rate publish karta hai — 999 purity per 10 gram INR. Formula: international spot (LBMA) ÷ 31.1035 × USD/INR × 10 + import duty (12.5%) + GST (3%). MCX futures aur retail jeweller rate isi ke qareeb settle hote hain." },
    { q: "Union Budget 2026 mein gold silver ka kya announcement hua?", a: "Budget-related changes in customs duty, LTCG treatment, and Sovereign Gold Bond scheme har year Feb 1 par announce hote hain. Latest live impact analysis is site ke India hub ke news section mein cover hoti hai — aaj ki effective import duty + GST rate live rate calculation mein already factored hai." },
    { q: "Aaj ka sone ka bhav kya hai India mein?", a: "Aaj ka 24 carat sone ka bhav India mein is page ke top card mein per 10 gram, per 1 gram aur per tola — teeno units mein live show ho raha hai. 22 carat aur 18 carat sone ka bhav bhi wahin breakdown table mein alag alag visible hai; rate har 10 minute par international XAU spot + USD/INR se refresh hota hai." },
    { q: "22 carat sone ka bhav aaj ka kitna hai?", a: "22 carat sone ka bhav = 24 carat rate × 0.9167 (yani 91.67% purity, 916 hallmark). Aaj ka live 22 carat figure is page ke 'Full national rate breakdown' mein Delhi, Mumbai, Chennai, Kolkata aur baaki metros ke liye alag alag visible hai — per 10 gram aur per gram dono." },
    { q: "1 tola sone ka bhav India mein aaj kitna hai?", a: "1 tola = 11.6638 gram. Aaj ka 1 tola sone ka bhav India mein per-gram rate × 11.6638 se calculate hota hai — ye page ke live headline aur karat cards mein 24K, 22K, 21K teeno ke liye ready-calculated show ho raha hai. Tola unit India mein temple + heritage jewellery ke liye still commonly quote hoti hai." },
    { q: "Sone ka bhav kab kam hoga India mein?", a: "Short-term (1–4 week) direction international spot (XAU/USD) aur USD/INR ke daily move ka combination hoti hai. Aksar Fed rate-cut expectation + weakening dollar sone ka bhav gira dete hain, lekin agar rupee bhi weak ho to India mein INR price high hi rehti hai. Long-term buyer ke liye SIP style monthly buy timing ki chinta se better hoti hai." },
    { q: "Live sone ka bhav aur today sone ka bhav mein kya difference hai?", a: "Dono same hain — 'live sone ka bhav' aur 'today sone ka bhav' aaj ke real-time gold price ko refer karte hain. Is page ka rate international spot + live FX se har 10 minute par refresh hota hai, isliye kabhi bhi kholne par current market ke closest figure milta hai." },
  ],
  uae: [
    { q: "Dubai Gold Souk mein price site ke rate se different kyun hai?", a: "Dubai Gold Souk (Deira) live wholesale rate follow karta hai — jewellery pieces par 6–15% making charge (design complexity + labour) add hoti hai. Investment coins / bars (Kilo bars, Emirates Gold) par sirf 0.5–1.5% dealer premium hoti hai. Ye site ka live per-gram rate wohi wholesale base hai." },
  ],
  saudi: [
    { q: "Saudi mein gold VAT ka impact kya hai?", a: "KSA mein 15% VAT applies to jewellery (making + design), but investment-grade 999 purity gold bars / coins are VAT-exempt (2018 GCC framework). Is wajah se Saudi investment gold globally sabse tight-spread markets mein se ek hai — retail jeweller price mein 15% VAT + 3–8% making dono add hote hain." },
  ],
};

